import {
  createStyles,
  Theme,
  Tooltip,
  WithStyles,
  withStyles
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { Build, Delete, Edit } from '@material-ui/icons';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import axios from '../../axios-pod';
import SubheadingBar from '../../components/SubheadingBar/SubheadingBar';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog/ConfirmationDialog';
import MainHeading from '../../components/UI/MainHeading/MainHeading';
import MainSection from '../../components/UI/MainSection/MainSection';
import MyDialog from '../../components/UI/MyDialog/MyDialog';
import MyTable from '../../components/UI/MyTable/MyTable';
import MyTableLoader from '../../components/UI/MyTableLoader/MyTableLoader';
import MyTooltip from '../../components/UI/MyTooltip/MyTooltip';
import SubHeading from '../../components/UI/SubHeading/SubHeading';
import SubSection from '../../components/UI/SubSection/SubSection';
import {
  IAgent,
  IConfirmationDialogState,
  IState,
  IUser
} from '../../models/states';
import {
  addAgent,
  deleteAgent,
  fetchAgents,
  fetchAgentsCancel,
  fetchSkynetConfig,
  updateAgent,
  updateSkynetConfig
} from '../../store/actions/agentAction';
import {
  ConfirmationTypes,
  initialConfirmationState,
  rowIndicators,
  UserRoles
} from '../../utilities/utilities';
import withErrorHandler from '../../utilities/withErrorHandler';
import AgentForm from './AgentForm/AgentForm';
import ConfigEditor from './ConfigEditor';

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    margin: {
      marginRight: '2rem',
      width: '25rem'
    },
    actionWrapper: { marginLeft: '-1rem', display: 'flex' },
    lightTooltip: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11
    }
  });
enum editorType {
  Agent,
  Skynet
}
class AgentManagement extends React.Component<
  IAssetManagementProps,
  IAgentManagementState
> {
  constructor(props: IAssetManagementProps) {
    super(props);
    this.state = {
      searchText: '',
      openEditorConfig: false,
      isOpenAgentForm: false,
      selectedAgent: undefined,
      currentConfig: '{}',
      editorType: editorType.Agent,
      confirmationDialogProps: initialConfirmationState
    };
  }
  createTableData = () => {
    return Object.values(this.props.agentMap)
      .filter(
        agent =>
          agent.customer
            .toLowerCase()
            .indexOf(this.state.searchText.toLowerCase()) !== -1 ||
          agent.sentinel_id
            .toLowerCase()
            .indexOf(this.state.searchText.toLowerCase()) !== -1
      )
      .map((agent: IAgent, index: number) => {
        return {
          ...agent,
          dataKey: index,
          last_heartbeat: agent.last_heartbeat
            ? moment.unix(agent.last_heartbeat).format('YYYY-MM-DD  HH:mm:ss')
            : 'NA',
          last_registered: moment
            .unix(agent.last_registered)
            .format('YYYY-MM-DD  HH:mm:ss'),
          agentConfig: (
            <div className={this.props.classes.actionWrapper}>
              <MyTooltip title={'Edit agent config'}>
                <IconButton
                  onClick={() =>
                    this.handleOpenConfigEdit(
                      agent.sentinel_id,
                      editorType.Agent
                    )
                  }
                >
                  <Edit />
                </IconButton>
              </MyTooltip>
              <MyTooltip title={'Edit skynet config'}>
                <IconButton
                  onClick={() =>
                    this.handleOpenConfigEdit(agent.customer, editorType.Skynet)
                  }
                >
                  <Build />
                </IconButton>
              </MyTooltip>
              <MyTooltip title={'Delete agent'}>
                <IconButton
                  onClick={() => this.handleDeleteAgent(agent.customer)}
                >
                  <Delete />
                </IconButton>
              </MyTooltip>
            </div>
          ),
          rowIndicator: agent.state === 'dead' ? rowIndicators.YELLOW : 'green'
        };
      });
  };
  handleOpenConfigEdit = (agentId: string, type: editorType) => {
    const agent = this.props.agentMap[agentId];

    const skynet = this.props.ruleEngineMap[agentId];
    const config =
      type === editorType.Agent ? agent.config : skynet ? skynet.rules : {};
    this.setState({
      openEditorConfig: true,
      editorType: type,
      selectedAgent: agentId,
      currentConfig: config ? JSON.stringify(config, null, 2) : '{}'
    });
  };
  handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchText: event.target.value.toLowerCase() });
  };

  handleCloseEditorConfig = () => {
    this.setState({ openEditorConfig: false });
  };

  handleConfigChange = newValue => {
    this.setState({ currentConfig: newValue });
  };

  handleFormOpen = () => {
    this.setState({ isOpenAgentForm: true });
  };

  handleFormClose = () => {
    this.setState({ isOpenAgentForm: false });
  };

  handleLoadDefaultConfig = () => {
    const { defaultAgent, defaultRuleEngine } = this.props;
    const config =
      this.state.editorType === editorType.Agent
        ? defaultAgent.config
        : defaultRuleEngine.rules;
    this.setState({
      currentConfig: config ? JSON.stringify(config, null, 2) : '{}'
    });
  };

  renderDeleteAgentContent = () => {
    return (
      <Typography>{`Are you sure you want to delete the agent`}?</Typography>
    );
  };

  handleDeleteAgent = (customer: string) => {
    this.setState({
      confirmationDialogProps: {
        type: ConfirmationTypes.DELETE_SCREEN,
        open: true,
        onProceedDialog: () => {
          this.props.deleteAgent(customer);
          this.handleCloseConfirmationDialog();
        },
        onCloseDialog: this.handleCloseConfirmationDialog,
        content: this.renderDeleteAgentContent(),
        title: 'Delete Agent'
      }
    });
  };
  handleCloseConfirmationDialog = () => {
    this.setState({ confirmationDialogProps: initialConfirmationState });
  };

  renderConfirmationDialog = () => {
    switch (this.state.confirmationDialogProps.type) {
      case ConfirmationTypes.DELETE_SCREEN:
        return this.renderDeleteAgentContent();
      default:
        return null;
    }
  };

  handleResetConfig = () => {
    const { agentMap, ruleEngineMap } = this.props;
    const configMap =
      this.state.editorType === editorType.Agent ? agentMap : ruleEngineMap;
    const conf = configMap[this.state.selectedAgent];
    const currentConfig =
      this.state.editorType === editorType.Agent
        ? conf.config
        : conf
        ? conf.rules
        : undefined;
    this.setState({
      currentConfig: currentConfig
        ? JSON.stringify(currentConfig, null, 2)
        : '{}'
    });
  };
  handleSubmit = (agent: IAgent) => {
    this.props.addAgent(agent);
    this.handleFormClose();
  };
  handleSaveAgentConfig = () => {
    let payload = {};
    if (this.state.editorType === editorType.Agent) {
      payload = {
        config: this.state.currentConfig,
        sentinel_id: this.state.selectedAgent,
        user_id: this.props.user.userId
      };
      this.props.updateAgent(payload);
    } else {
      payload = {
        config: this.state.currentConfig,
        customer: this.state.selectedAgent,
        user_id: this.props.user.userId
      };
      this.props.updateSkynetConfig(payload);
    }
    this.handleCloseEditorConfig();
  };
  componentDidMount() {
    this.props.fetchAgents();
    this.props.fetchSkynetConfig();
  }
  componentWillUnmount() {
    this.props.fetchAgentsCancel();
  }
  public render() {
    const { user } = this.props;
    const { isOpenAgentForm } = this.state;
    const agentTableHeader = [
      {
        label: 'Customer',
        dataKey: 'customer',
        isFilter: false,
        isSort: true,
        tooltip: true,
        lowercase: true
      },
      {
        label: 'Agent Id',
        dataKey: 'sentinel_id',
        isFilter: false,
        isSort: true,
        tooltip: true,
        lowercase: true
      },
      {
        label: 'Last Registered',
        dataKey: 'last_registered',
        isFilter: false,
        isSort: true,
        tooltip: true,
        lowercase: true
      },
      {
        label: 'Last Heartbeat',
        dataKey: 'last_heartbeat',
        isFilter: false, // todo: change to true
        isSort: true,
        tooltip: true,
        lowercase: true
      },
      {
        label: 'Status',
        dataKey: 'state',
        isFilter: false, // change to true
        isSort: true
      }
    ];
    if (user && user.role === UserRoles.ADMIN) {
      agentTableHeader.push({
        label: 'Actions',
        dataKey: 'agentConfig',
        isFilter: false, // change to true
        isSort: false
      });
    }
    const filteredData = this.createTableData();
    return (
      <>
        <MainSection>
          <MainHeading
            searchPlaceholder={'Search by customer name, agent id ...'}
            onSearch={this.handleSearch}
            mainHeading={'Agent Management'}
            onClickButton={this.handleFormOpen}
            showButton={user && user.role === UserRoles.ADMIN}
            buttonText={'Add Agent'}
          />
          <SubSection>
            <SubHeading>
              <SubheadingBar
                title={'Agents'}
                filteredItems={filteredData.length}
                totalItems={this.props.agentIds.length}
                filters={{}}
                toggleFilter={() => null}
                showLoading={true}
                loading={this.props.isFetching}
                lastFetched={this.props.lastFetched}
              />
            </SubHeading>
            {this.props.isFetching && this.props.fetchCount === 1 ? (
              <MyTableLoader />
            ) : (
              <MyTable
                tRows={filteredData}
                multiSelect={false}
                tHead={agentTableHeader}
                order={'asc'}
                orderBy={'customer'}
              />
            )}
          </SubSection>
          <MyDialog
            open={this.state.openEditorConfig}
            onClose={this.handleCloseEditorConfig}
            title={`Edit ${editorType[this.state.editorType]} Config`}
            maxWidth={'lg'}
          >
            <ConfigEditor
              currentConfig={this.state.currentConfig}
              onChange={this.handleConfigChange}
              onLoadDefaultConfig={this.handleLoadDefaultConfig}
              onResetConfig={this.handleResetConfig}
              onSaveEditorConfig={this.handleSaveAgentConfig}
            />
          </MyDialog>
          <ConfirmationDialog {...this.state.confirmationDialogProps}>
            {this.renderConfirmationDialog()}
          </ConfirmationDialog>
        </MainSection>
        {isOpenAgentForm && (
          <MyDialog
            open={isOpenAgentForm}
            onClose={this.handleFormClose}
            title={'Add Agent'}
          >
            <AgentForm onSubmit={this.handleSubmit} />
          </MyDialog>
        )}
      </>
    );
  }
}
interface IAgentManagementStateProps {
  agentIds: string[];
  agentMap: { [key: string]: IAgent };
  isFetching: boolean;
  fetchCount: number;
  lastFetched: string;
  defaultAgent: IAgent;
  defaultRuleEngine: any;
  ruleEngineMap: { [key: string]: any };
  user: IUser;
}
interface IAgentManagementDispatchProps {
  fetchAgents: () => any;
  fetchAgentsCancel: () => any;
  addAgent: (payload: any) => any;
  updateAgent: (payload: any) => any;
  fetchSkynetConfig: () => any;
  updateSkynetConfig: (payload: any) => any;
  deleteAgent: (customer: string) => any;
}
interface IAgentManagementState {
  searchText: string;
  openEditorConfig: boolean;
  isOpenAgentForm: boolean;
  selectedAgent: string;
  currentConfig: any;
  editorType: editorType; // 1 for agent and 2 for skynet
  confirmationDialogProps: IConfirmationDialogState;
}
const mapStateToProps = (state: IState): IAgentManagementStateProps => {
  return {
    agentIds: state.agents.agentId,
    agentMap: state.agents.agentMap,
    isFetching: state.agents.isFetching,
    fetchCount: state.agents.fetchCount,
    lastFetched: state.agents.lastFetched,
    defaultAgent: state.agents.defaultAgent,
    user: state.user.userInfo,
    defaultRuleEngine: state.agents.defaultRuleEngine,
    ruleEngineMap: state.agents.ruleEngineMap
  };
};
type IAssetManagementProps = WithStyles<typeof styles> &
  IAgentManagementDispatchProps &
  IAgentManagementStateProps;
const mapDispatchToProps: IAgentManagementDispatchProps = {
  fetchAgents,
  fetchAgentsCancel,
  addAgent,
  updateAgent,
  fetchSkynetConfig,
  updateSkynetConfig,
  deleteAgent
};
export default withStyles(styles)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withErrorHandler(AgentManagement, axios))
);
